/**
 * Página de Autenticação - Login/Cadastro/Recuperação de Senha
 * Layout split-screen moderno com área de branding e formulário
 * Responsivo: split 50/50 em desktop, full-width em mobile
 * Usa branding dinâmico (logo, ícone, nome) da tabela system_settings
 * Inclui versículos bíblicos NTLH no lado esquerdo
 */
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useSystemSettings } from '@/hooks/useSystemSettings';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';

import { Label } from '@/components/ui/label';
import { showToast } from '@/lib/toast';
import { translateAuthError } from '@/lib/auth-utils';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { MessageSquare, Mail, Lock, User, ArrowRight, Loader2, Quote, ArrowLeft, RefreshCw, Phone, Download, Share, Plus, Smartphone, X, ChevronRight } from 'lucide-react';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { PhoneInput } from '@/components/ui/phone-input';
import { z } from 'zod';
import Lottie from 'lottie-react';
import chatSupportAnimation from '@/assets/animations/chat-support.json';
import { createLogger } from '@/lib/logger';
// Helper de normalização de telefone BR (mesmo padrão dos demais formulários)
import { normalizeBrazilianPhone } from '@/lib/phone-utils';
// Regras de senha forte centralizadas (mesmas exigidas pelo backend) + checklist
import { strongPasswordSchema, translatePasswordError, isExpiredResetLinkError } from '@/lib/passwordRules';
import { PasswordRequirements } from '@/components/auth/PasswordRequirements';

// Logger para a página de autenticação
const authLogger = createLogger('Auth');

// Schemas de validação
const emailSchema = z.string().email('Email inválido');
// Senha legada (login): senhas antigas podem não seguir a política forte
const passwordSchema = z.string().min(6, 'Senha deve ter pelo menos 6 caracteres');

/**
 * Versículos da Bíblia - Nova Tradução na Linguagem de Hoje (NTLH)
 * Rotacionados aleatoriamente a cada 2 minutos
 */
const versiculosBiblia = [
  { texto: "Confie no SENHOR de todo o coração e não se apoie na sua própria inteligência.", referencia: "Provérbios 3:5" },
  { texto: "Eu sei muito bem os planos que tenho para vocês, planos de paz e não de desgraça, planos para dar a vocês um futuro e uma esperança.", referencia: "Jeremias 29:11" },
  { texto: "O SENHOR é o meu pastor: nada me faltará.", referencia: "Salmos 23:1" },
  { texto: "Tudo posso naquele que me fortalece.", referencia: "Filipenses 4:13" },
  { texto: "Não tenha medo, pois eu estou com você; não fique aflito, pois eu sou o seu Deus. Eu dou forças a você e o ajudo.", referencia: "Isaías 41:10" },
  { texto: "Entreguem todas as suas preocupações a Deus, pois ele cuida de vocês.", referencia: "1 Pedro 5:7" },
  { texto: "O amor é paciente, é bondoso. O amor não é ciumento, nem orgulhoso, nem vaidoso.", referencia: "1 Coríntios 13:4" },
  { texto: "Porque Deus amou o mundo tanto, que deu o seu único Filho, para que todo aquele que nele crer não morra, mas tenha a vida eterna.", referencia: "João 3:16" },
  { texto: "Peçam, e vocês receberão; busquem, e vocês acharão; batam, e a porta será aberta.", referencia: "Mateus 7:7" },
  { texto: "A fé é a certeza de que vamos receber as coisas que esperamos e a prova de que existem coisas que não podemos ver.", referencia: "Hebreus 11:1" },
  { texto: "Venham a mim, todos vocês que estão cansados de carregar as suas pesadas cargas, e eu lhes darei descanso.", referencia: "Mateus 11:28" },
  { texto: "O SENHOR abençoe você e o guarde; o SENHOR faça brilhar o seu rosto sobre você e tenha misericórdia de você.", referencia: "Números 6:24-25" },
];

// Badges flutuantes para exibir ao redor da animação
const floatingBadges = [
  { text: 'Omnichannel', position: 'top-16 left-8', delay: '0s' },
  { text: 'Automações', position: 'top-32 right-12', delay: '0.5s' },
  { text: 'Métricas', position: 'bottom-40 left-16', delay: '1s' },
  { text: 'Segurança', position: 'bottom-24 right-8', delay: '1.5s' },
];

const Auth = () => {
  const { t } = useTranslation('auth');
  // Hook para instalação do PWA (botão no topo direito da tela de login)
  const { isInstallable, isInstalled, isIOS, promptInstall, isInstalling } = usePWAInstall();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState(''); // Telefone para contato posterior
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
    confirmPassword?: string;
    fullName?: string;
    phone?: string;
  }>({});
  
  // Estado para modal de recuperação de senha
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  
  // Estado para modo de redefinição de senha (após clicar no link do email)
  const [isResetMode, setIsResetMode] = useState(false);
  // Link de redefinição vencido, já usado ou sem sessão ativa
  const [resetLinkInvalid, setResetLinkInvalid] = useState(false);
  
  // Estado para tela de confirmação de email após cadastro
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  
  // Estado para reenvio de email de confirmação
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  
  // Estado para dispensar o banner de instalação do PWA
  const [isPWADismissed, setIsPWADismissed] = useState(false);

  // Estado para exibir tela de reenvio quando email não confirmado é detectado no login
  const [emailNotConfirmedEmail, setEmailNotConfirmedEmail] = useState<string | null>(null);
  
  // Estado para versículo atual (randômico)
  const [versiculoIndex, setVersiculoIndex] = useState(() => 
    Math.floor(Math.random() * versiculosBiblia.length)
  );

  const {
    signIn,
    signUp,
    resetPassword,
    updatePassword,
    resendConfirmationEmail,
    user,
    loading: authLoading
  } = useAuth();
  const {
    branding
  } = useSystemSettings();
  const navigate = useNavigate();

  // Verifica se está no modo de reset de senha (via URL)
  // Também verifica se email foi confirmado (vindo do fluxo de convite)
  // Suporte a parâmetros de modo via URL: ?mode=register ou ?mode=reset
  useEffect(() => {
    const mode = searchParams.get('mode');
    
    // Abre direto na tela de cadastro quando mode=register
    if (mode === 'register') {
      setIsLogin(false);
    } else if (mode === 'reset') {
      setIsResetMode(true);
      // Detecta link vencido/já usado (o servidor devolve o erro no hash ou na query)
      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const errCode = hashParams.get('error_code') || searchParams.get('error_code') || '';
      const errDesc = hashParams.get('error_description') || searchParams.get('error_description') || '';
      const err = hashParams.get('error') || searchParams.get('error') || '';
      if (err || errCode || isExpiredResetLinkError(`${errCode} ${errDesc}`)) {
        setResetLinkInvalid(true);
      }
    }
    
    // Detecta confirmação de email vinda do fluxo de convite
    const emailConfirmed = searchParams.get('email_confirmed');
    if (emailConfirmed === 'true') {
      showToast.success('Email confirmado com sucesso!', 'Faça login para continuar e acessar sua equipe.');
      // Limpa o parâmetro da URL para não exibir novamente
      searchParams.delete('email_confirmed');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Redireciona quando user existe E loading terminou (exceto no modo reset)
  useEffect(() => {
    if (user && !authLoading && !isResetMode) {
      navigate('/');
    }
  }, [user, authLoading, navigate, isResetMode]);

  // Rotaciona versículos a cada 2 minutos
  useEffect(() => {
    const interval = setInterval(() => {
      setVersiculoIndex(prev => {
        let newIndex;
        do {
          newIndex = Math.floor(Math.random() * versiculosBiblia.length);
        } while (newIndex === prev && versiculosBiblia.length > 1);
        return newIndex;
      });
    }, 120000); // 2 minutos

    return () => clearInterval(interval);
  }, []);

  // Countdown do cooldown de reenvio de email
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // ============================================================
  // Processa tokens de recuperação de senha do hash da URL
  // Como detectSessionInUrl=false no client, precisamos processar manualmente
  // os tokens que vêm no hash após clicar no link de recuperação de senha
  // ============================================================
  useEffect(() => {
    const processHashTokens = async () => {
      const hash = window.location.hash;
      
      // Verifica se há tokens no hash (formato: #access_token=...&refresh_token=...)
      if (!hash || !hash.includes('access_token')) {
        return;
      }
      
      // Extrair tokens do hash
      const hashParams = new URLSearchParams(hash.substring(1)); // Remove o #
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const type = hashParams.get('type'); // recovery, signup, invite
      
      // Se não temos tokens válidos, sair
      if (!accessToken || !refreshToken) {
        authLogger.warn('Hash com tokens incompletos');
        return;
      }
      
      authLogger.info('Tokens detectados no hash, tipo:', type);
      
      try {
        // Estabelecer sessão com os tokens
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        
        if (error) {
          authLogger.error('Erro ao estabelecer sessão:', error.message);
          showToast.error('Erro ao processar link', error.message);
          return;
        }
        
        authLogger.info('Sessão estabelecida com sucesso');
        
        // Limpar o hash da URL para evitar reprocessamento
        window.history.replaceState(
          null, 
          '', 
          window.location.pathname + window.location.search
        );
        
        // Se for recovery, garantir que está no modo reset
        if (type === 'recovery') {
          setIsResetMode(true);
        }
        
      } catch (err) {
        authLogger.error('Erro ao processar tokens:', err);
      }
    };
    
    processHashTokens();
  }, []); // Executa apenas uma vez no mount

  const versiculoAtual = versiculosBiblia[versiculoIndex];

  /**
   * Valida os campos do formulário
   */
  const validateForm = () => {
    const newErrors: {
      email?: string;
      password?: string;
      confirmPassword?: string;
      fullName?: string;
      phone?: string;
    } = {};
    
    // Se estiver no modo reset, validar apenas senha e confirmação
    // Usa o schema FORTE (mesmas regras do backend) para barrar antes do submit
    if (isResetMode) {
      const passwordResult = strongPasswordSchema.safeParse(password);
      if (!passwordResult.success) {
        newErrors.password = passwordResult.error.errors[0].message;
      }
      if (password !== confirmPassword) {
        newErrors.confirmPassword = 'As senhas não conferem';
      }
    } else {
      const emailResult = emailSchema.safeParse(email);
      if (!emailResult.success) {
        newErrors.email = emailResult.error.errors[0].message;
      }
      // Login aceita senha legada; cadastro exige senha forte (backend recusa fraca)
      const schemaSenha = isLogin ? passwordSchema : strongPasswordSchema;
      const passwordResult = schemaSenha.safeParse(password);
      if (!passwordResult.success) {
        newErrors.password = passwordResult.error.errors[0].message;
      }
      if (!isLogin && !fullName.trim()) {
        newErrors.fullName = 'Nome é obrigatório';
      }
      // Validação de telefone obrigatório no cadastro
      if (!isLogin) {
        const phoneDigits = phone.replace(/\D/g, '');
        if (!phoneDigits || phoneDigits.length < 10) {
          newErrors.phone = 'Telefone válido é obrigatório';
        }
        // Validação de confirmação de senha no cadastro (padrão SaaS)
        if (!confirmPassword) {
          newErrors.confirmPassword = 'Confirme sua senha';
        } else if (password !== confirmPassword) {
          newErrors.confirmPassword = 'As senhas não conferem';
        }
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Processa o submit do formulário (login ou cadastro)
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    
    try {
      // Modo de redefinição de senha
      if (isResetMode) {
        // Confere se a sessão de redefinição ainda existe antes de chamar o servidor
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData.session) {
          setResetLinkInvalid(true);
          return;
        }
        const { error } = await updatePassword(password);
        if (error && isExpiredResetLinkError(error.message)) {
          setResetLinkInvalid(true);
          return;
        }
        if (error) {
          // Rede de segurança: traduz mensagens cruas do backend para PT-BR
          showToast.error('Erro ao redefinir senha', translatePasswordError(error.message));
        } else {
          showToast.success('Senha atualizada!', 'Sua senha foi redefinida com sucesso. Faça login.');
          // Limpar modo reset e redirecionar para login
          setIsResetMode(false);
          setSearchParams({});
          setPassword('');
          setConfirmPassword('');
        }
        return;
      }
      
      // Login ou cadastro normal
      if (isLogin) {
        const { error } = await signIn(email, password);
        if (error) {
          // Detectar credenciais inválidas (Supabase usa diferentes formatos de mensagem)
          const errorMsg = error.message?.toLowerCase() || '';
          const isInvalidCredentials = 
            errorMsg.includes('invalid') ||
            errorMsg.includes('credentials') ||
            errorMsg.includes('wrong password') ||
            errorMsg.includes('user not found');
          
          // Detectar email não confirmado
          const isEmailNotConfirmed = 
            errorMsg.includes('email not confirmed') ||
            errorMsg.includes('not confirmed');
          
          if (isEmailNotConfirmed) {
            // Guarda o email para permitir reenvio na tela dedicada
            setEmailNotConfirmedEmail(email);
            showToast.error(
              'Email não confirmado',
              'Use o botão abaixo para reenviar o link de confirmação.'
            );
            return; // Não continua o fluxo
          } else if (isInvalidCredentials) {
            showToast.error(
              'Credenciais inválidas',
              'Email ou senha incorretos. Verifique e tente novamente.'
            );
          } else {
            showToast.error('Erro ao entrar', error.message);
          }
        } else {
          showToast.success('Bem-vindo!', 'Login realizado com sucesso');
        }
      } else {
        // Cadastro de novo usuário — normaliza telefone antes de persistir
        // para evitar "+" e máscara em profiles.phone (herdado por contacts/CRM)
        const normalizedPhone = normalizeBrazilianPhone(phone);
        const { data, error } = await signUp(email, password, fullName, normalizedPhone);
        
        // 1. Verificar erro explícito do Supabase
        if (error) {
          const translatedError = translateAuthError(error.message, t);
          showToast.error(t('errors.genericError'), translatedError);
          return;
        }
        
        // 2. Verificar se email já existe (Supabase retorna identities=[] neste caso)
        // Por segurança, Supabase não retorna erro para email existente
        if (data?.user?.identities?.length === 0) {
          showToast.error(
            'Email já cadastrado',
            'Este email já está registrado. Faça login ou recupere sua senha.'
          );
          return;
        }
        
        // 3. Sucesso real - conta criada, exibir tela de confirmação
        setRegisteredEmail(email);
        setShowEmailConfirmation(true);
        // Limpar campos do formulário
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setFullName('');
        setPhone('');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Processa solicitação de recuperação de senha
   */
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const emailResult = emailSchema.safeParse(forgotPasswordEmail);
    if (!emailResult.success) {
      showToast.error('Email inválido', 'Digite um email válido');
      return;
    }
    
    setForgotPasswordLoading(true);
    try {
      const { error } = await resetPassword(forgotPasswordEmail);
      if (error) {
        showToast.error('Erro ao enviar email', error.message);
      } else {
        showToast.success(
          'Email enviado!',
          'Verifique sua caixa de entrada para redefinir sua senha'
        );
        setShowForgotPassword(false);
        setForgotPasswordEmail('');
      }
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  /**
   * Reenvia email de confirmação de cadastro
   * Cooldown de 60 segundos para evitar spam
   */
  const handleResendEmail = async () => {
    if (!registeredEmail || resendCooldown > 0) return;
    
    setResendLoading(true);
    try {
      const { error } = await resendConfirmationEmail(registeredEmail);
      if (error) {
        showToast.error(
          t('emailConfirmation.resendError'),
          error.message
        );
      } else {
        showToast.success(
          t('emailConfirmation.resendSuccess'),
          t('emailConfirmation.resendSuccessMessage')
        );
        // Cooldown de 60 segundos para evitar spam
        setResendCooldown(60);
      }
    } finally {
      setResendLoading(false);
    }
  };

  /**
   * Reenvia email de confirmação quando detectado no login
   * Reutiliza a mesma lógica do handleResendEmail
   */
  const handleResendUnconfirmedEmail = async () => {
    if (!emailNotConfirmedEmail || resendCooldown > 0) return;
    
    setResendLoading(true);
    try {
      const { error } = await resendConfirmationEmail(emailNotConfirmedEmail);
      if (error) {
        showToast.error(
          t('emailConfirmation.resendError'),
          error.message
        );
      } else {
        showToast.success(
          t('emailConfirmation.resendSuccess'),
          t('emailConfirmation.resendSuccessMessage')
        );
        setResendCooldown(60);
      }
    } finally {
      setResendLoading(false);
    }
  };

  // Renderiza tela de reenvio quando email não confirmado é detectado no login
  if (emailNotConfirmedEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-md text-center animate-auth-fade-in">
          {/* Logo centralizada */}
          <div className="flex justify-center mb-8">
           {(branding.login_logo_url || branding.logo_url) ? (
             <img src={branding.login_logo_url || branding.logo_url!} alt={branding.app_name} className="h-[80px] object-contain" />
            ) : (
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-primary/10">
                  {branding.icon_url ? (
                    <img src={branding.icon_url} alt="" className="h-8 w-8 object-contain" />
                  ) : (
                    <MessageSquare className="h-8 w-8 text-primary" />
                  )}
                </div>
                <span className="text-2xl font-bold text-foreground">{branding.app_name}</span>
              </div>
            )}
          </div>

          {/* Ícone de email com cor de alerta */}
          <div className="mx-auto mb-6 p-4 rounded-full bg-orange-100 dark:bg-orange-900/30 w-fit">
            <Mail className="h-12 w-12 text-orange-500" />
          </div>

          {/* Título */}
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
            {t('emailConfirmation.title')}
          </h2>

          {/* Descrição */}
          <p className="text-muted-foreground mb-6 leading-relaxed">
            {t('emailConfirmation.subtitle')}
            <br />
            <span className="font-semibold text-foreground">{emailNotConfirmedEmail}</span>
          </p>

          {/* Instruções */}
          <div className="bg-muted/50 rounded-xl p-4 mb-6 text-left">
            <h3 className="font-semibold mb-2 text-foreground">Próximos passos:</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
              <li>{t('emailConfirmation.instructions.step1')}</li>
              <li>{t('emailConfirmation.instructions.step2')}</li>
              <li>{t('emailConfirmation.instructions.step3')}</li>
              <li>{t('emailConfirmation.instructions.step4')}</li>
            </ol>
          </div>

          {/* Dica de spam */}
          <p className="text-xs text-muted-foreground mb-6">
            {t('emailConfirmation.checkSpam')}
          </p>

          {/* Botão de reenviar email */}
          <Button
            onClick={handleResendUnconfirmedEmail}
            disabled={resendLoading || resendCooldown > 0}
            variant="outline"
            className="w-full h-12 rounded-xl border-primary/30 hover:bg-primary/5 mb-3"
          >
            {resendLoading ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                {t('emailConfirmation.resendLoading')}
              </>
            ) : resendCooldown > 0 ? (
              t('emailConfirmation.resendCooldown', { seconds: resendCooldown })
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                {t('emailConfirmation.resendButton')}
              </>
            )}
          </Button>

          {/* Botão voltar para login */}
          <Button
            onClick={() => setEmailNotConfirmedEmail(null)}
            className="w-full h-12 rounded-xl bg-gradient-to-r from-[#0a2540] to-[#1a3a5c] hover:from-[#0d2d4d] hover:to-[#1f4267]"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('emailConfirmation.backToLogin')}
          </Button>
        </div>
      </div>
    );
  }

  // Renderiza tela de confirmação de email após cadastro
  if (showEmailConfirmation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-md text-center animate-auth-fade-in">
          {/* Logo centralizada */}
          <div className="flex justify-center mb-8">
           {(branding.login_logo_url || branding.logo_url) ? (
             <img src={branding.login_logo_url || branding.logo_url!} alt={branding.app_name} className="h-[80px] object-contain" />
            ) : (
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-primary/10">
                  {branding.icon_url ? (
                    <img src={branding.icon_url} alt="" className="h-8 w-8 object-contain" />
                  ) : (
                    <MessageSquare className="h-8 w-8 text-primary" />
                  )}
                </div>
                <span className="text-2xl font-bold text-foreground">{branding.app_name}</span>
              </div>
            )}
          </div>

          {/* Ícone de email */}
          <div className="mx-auto mb-6 p-4 rounded-full bg-primary/10 w-fit">
            <Mail className="h-12 w-12 text-primary" />
          </div>

          {/* Título */}
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
            Verifique seu email
          </h2>

          {/* Descrição */}
          <p className="text-muted-foreground mb-6 leading-relaxed">
            Enviamos um link de confirmação para:
            <br />
            <span className="font-semibold text-foreground">{registeredEmail}</span>
          </p>

          {/* Instruções */}
          <div className="bg-muted/50 rounded-xl p-4 mb-6 text-left">
            <h3 className="font-semibold mb-2 text-foreground">Próximos passos:</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
              <li>Acesse sua caixa de entrada de email</li>
              <li>Procure o email de confirmação do {branding.app_name}</li>
              <li>Clique no link "Confirmar email" dentro do email</li>
              <li>Retorne aqui e faça login normalmente</li>
            </ol>
          </div>

          {/* Dica de spam */}
          <p className="text-xs text-muted-foreground mb-6">
            Não encontrou? Verifique sua pasta de <strong>spam</strong> ou <strong>lixo eletrônico</strong>.
          </p>

          {/* Botão de reenviar email */}
          <Button
            onClick={handleResendEmail}
            disabled={resendLoading || resendCooldown > 0}
            variant="outline"
            className="w-full h-12 rounded-xl border-primary/30 hover:bg-primary/5 mb-3"
          >
            {resendLoading ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                {t('emailConfirmation.resendLoading')}
              </>
            ) : resendCooldown > 0 ? (
              t('emailConfirmation.resendCooldown', { seconds: resendCooldown })
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                {t('emailConfirmation.resendButton')}
              </>
            )}
          </Button>

          {/* Botão para ir ao login */}
          <Button
            onClick={() => {
              setShowEmailConfirmation(false);
              setIsLogin(true);
            }}
            className="w-full h-12 rounded-xl bg-gradient-to-r from-[#0a2540] to-[#1a3a5c] hover:from-[#0d3b66] hover:to-[#245f8a] text-white font-semibold"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Ir para login
          </Button>
        </div>
      </div>
    );
  }

  // Renderiza formulário de redefinição de senha
  if (isResetMode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-md animate-auth-fade-in">
          {/* Logo centralizada */}
          <div className="flex justify-center mb-8">
           {(branding.login_logo_url || branding.logo_url) ? (
              <img src={branding.login_logo_url || branding.logo_url!} alt={branding.app_name} className="h-auto max-h-[140px] max-w-[320px] object-contain" />
            ) : (
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-primary/10">
                  {branding.icon_url ? (
                    <img src={branding.icon_url} alt="" className="h-8 w-8 object-contain" />
                  ) : (
                    <MessageSquare className="h-8 w-8 text-primary" />
                  )}
                </div>
                <span className="text-2xl font-bold text-foreground">{branding.app_name}</span>
              </div>
            )}
          </div>

          {/* Cabeçalho */}
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
              Redefinir senha
            </h2>
            <p className="text-muted-foreground">
              Digite sua nova senha abaixo
            </p>
          </div>

          {/* Link vencido/já usado: orienta a solicitar um novo link em vez do formulário */}
          {resetLinkInvalid ? (
            <div className="space-y-5">
              <div className="border-l-4 border-destructive pl-4 py-2">
                <p className="font-semibold text-foreground">Este link expirou ou já foi usado</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Por segurança, cada link de redefinição vale uma única vez e por tempo limitado. Solicite um novo link para continuar.
                </p>
              </div>
              <Button
                type="button"
                className="w-full h-12"
                onClick={() => {
                  setResetLinkInvalid(false);
                  setIsResetMode(false);
                  setSearchParams({});
                  setShowForgotPassword(true);
                }}
              >
                Enviar novo link
              </Button>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Campo Nova Senha */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">
                Nova senha
              </Label>
              <PasswordInput
                id="password"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="h-12 rounded-xl border-border/50 bg-background transition-all focus:ring-2 focus:ring-primary/20 focus:border-primary"
                disabled={loading}
              />
              {/* Checklist de requisitos em tempo real (evita erro do backend) */}
              {password.length > 0 && <PasswordRequirements password={password} />}
              {errors.password && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <span className="inline-block w-1 h-1 rounded-full bg-destructive" />
                  {errors.password}
                </p>
              )}
            </div>

            {/* Campo Confirmar Senha */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-medium">
                Confirmar senha
              </Label>
              <PasswordInput
                id="confirmPassword"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="h-12 rounded-xl border-border/50 bg-background transition-all focus:ring-2 focus:ring-primary/20 focus:border-primary"
                disabled={loading}
              />

              {/* Aviso em tempo real quando a confirmação diverge da nova senha
                  (cobre também o erro de submit — por isso não há bloco errors.confirmPassword aqui) */}
              {confirmPassword.length > 0 && password !== confirmPassword && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <span className="inline-block w-1 h-1 rounded-full bg-destructive" />
                  As senhas não conferem
                </p>
              )}
            </div>

            {/* Botão de submit */}
            <Button 
              type="submit" 
              className="w-full h-12 rounded-xl bg-gradient-to-r from-[#0a2540] to-[#1a3a5c] hover:from-[#0d3b66] hover:to-[#245f8a] text-white font-semibold text-base shadow-lg shadow-[#0a2540]/30 transition-all duration-300 hover:shadow-xl hover:shadow-[#0a2540]/40 hover:scale-[1.02] active:scale-[0.98]" 
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Atualizando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Redefinir senha
                  <ArrowRight className="h-5 w-5" />
                </span>
              )}
            </Button>
          </form>
          )}

          {/* Link para voltar ao login */}
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => {
                setIsResetMode(false);
                setSearchParams({});
                setPassword('');
                setConfirmPassword('');
              }}
              className="text-sm text-primary hover:text-primary/80 transition-colors hover:underline inline-flex items-center gap-1"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar ao login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-background">
      {/* ========== ÁREA DE BRANDING (lado esquerdo) ========== */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Gradiente azul/turquesa elegante compatível com a logo */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a2540] via-[#1a3a5c] to-[#0d4a6b]" />
        
        {/* Overlay com padrão sutil */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.3) 2px, transparent 2px)`,
            backgroundSize: '50px 50px'
          }} />
        </div>

        {/* Elementos decorativos flutuantes - Efeito brilho turquesa/azul */}
        <div className="absolute top-20 left-20 w-32 h-32 bg-[#2dd4bf]/20 rounded-full blur-xl animate-auth-float" />
        <div className="absolute bottom-32 right-20 w-40 h-40 bg-[#3b82f6]/20 rounded-full blur-xl animate-auth-float-delayed" />
        <div className="absolute top-1/2 left-10 w-24 h-24 bg-[#2dd4bf]/15 rounded-full blur-lg animate-auth-float" />
        <div className="absolute top-40 right-32 w-20 h-20 bg-[#60a5fa]/15 rounded-full blur-lg animate-auth-float-delayed" />
        
        {/* Conteúdo do branding - Animação Lottie + Badges */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full w-full px-8">
          {/* Badges flutuantes ao redor da animação */}
          {floatingBadges.map((badge, index) => (
            <div
              key={index}
              className={`absolute ${badge.position} px-4 py-2 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white/90 text-sm font-medium shadow-lg animate-auth-float`}
              style={{ animationDelay: badge.delay }}
            >
              {badge.text}
            </div>
          ))}

          {/* Animação Lottie centralizada */}
          <div className="w-full max-w-md flex-shrink-0">
            <Lottie 
              animationData={chatSupportAnimation} 
              loop={true}
              className="w-full h-auto"
            />
          </div>

          {/* Balão de Versículo Bíblico */}
          <div className="mt-6 p-4 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 transition-all duration-500 max-w-md w-full">
            <div className="flex items-start gap-2">
              <Quote className="h-5 w-5 text-[#2dd4bf] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white/90 italic text-sm leading-relaxed">
                  "{versiculoAtual.texto}"
                </p>
                <p className="text-[#2dd4bf] text-xs mt-2 font-medium">
                  — {versiculoAtual.referencia}
                </p>
              </div>
            </div>
          </div>

          {/* Footer do branding */}
          <div className="mt-6 pt-4 border-t border-white/20 max-w-md w-full">
            <p className="text-white/70 text-xs text-center">
              Mais de 1.000 empresas confiam no UaizyChat para transformar seu atendimento
            </p>
          </div>
        </div>
      </div>

      {/* ========== ÁREA DO FORMULÁRIO (lado direito) ========== */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-8 lg:p-12">
        {/* Background sutil para mobile */}
        <div className="lg:hidden absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
        
        <div className="w-full max-w-md relative animate-auth-fade-in">
          {/* Banner premium de instalação do PWA - topo direito acima da logo */}
          {((isInstallable || isIOS) && !isInstalled && !isPWADismissed) && (
            <div className="absolute -top-6 right-0 z-10 animate-auth-fade-in">
              {/* Wrapper: Android usa botão direto, iOS usa Popover */}
              {isIOS && !isInstallable ? (
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="group relative flex items-center gap-3 px-4 py-3 rounded-xl border border-primary/20 bg-gradient-to-r from-primary/10 to-accent/10 shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer">
                      {/* Ícone com animação pulse */}
                      <div className="flex-shrink-0 p-2 rounded-lg bg-primary/15">
                        <Smartphone className="h-5 w-5 text-primary animate-pulse" />
                      </div>
                      {/* Textos */}
                      <div className="text-left">
                        <p className="text-sm font-semibold text-foreground leading-tight">Instalar App</p>
                        <p className="text-[11px] text-muted-foreground leading-tight">Acesse direto do seu celular</p>
                      </div>
                      {/* Seta indicativa */}
                      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      {/* Botão fechar (X) discreto */}
                      <span
                        role="button"
                        onClick={(e) => { e.stopPropagation(); setIsPWADismissed(true); }}
                        className="absolute -top-2 -right-2 p-1 rounded-full bg-muted hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-72">
                    <div className="space-y-3">
                      <h4 className="font-medium text-sm">Como instalar no iPhone/iPad</h4>
                      <ol className="space-y-2 text-xs text-muted-foreground">
                        <li className="flex items-center gap-2">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[10px] font-medium">1</span>
                          <span>Toque em <Share className="h-3 w-3 inline" /> <strong>Compartilhar</strong></span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[10px] font-medium">2</span>
                          <span>Toque em <Plus className="h-3 w-3 inline" /> <strong>Adicionar à Tela de Início</strong></span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[10px] font-medium">3</span>
                          <span>Toque em <strong>Adicionar</strong></span>
                        </li>
                      </ol>
                    </div>
                  </PopoverContent>
                </Popover>
              ) : (
                /* Android/Chrome - clique direto para instalar */
                <button
                  onClick={promptInstall}
                  disabled={isInstalling}
                  className="group relative flex items-center gap-3 px-4 py-3 rounded-xl border border-primary/20 bg-gradient-to-r from-primary/10 to-accent/10 shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer disabled:opacity-50"
                >
                  {/* Ícone com animação pulse */}
                  <div className="flex-shrink-0 p-2 rounded-lg bg-primary/15">
                    <Smartphone className="h-5 w-5 text-primary animate-pulse" />
                  </div>
                  {/* Textos */}
                  <div className="text-left">
                    <p className="text-sm font-semibold text-foreground leading-tight">
                      {isInstalling ? 'Instalando...' : 'Instalar App'}
                    </p>
                    <p className="text-[11px] text-muted-foreground leading-tight">Acesse direto do seu celular</p>
                  </div>
                  {/* Seta indicativa */}
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  {/* Botão fechar (X) discreto */}
                  <span
                    role="button"
                    onClick={(e) => { e.stopPropagation(); setIsPWADismissed(true); }}
                    className="absolute -top-2 -right-2 p-1 rounded-full bg-muted hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </span>
                </button>
              )}
            </div>
          )}
          {/* Logo centralizada */}
          <div className="flex justify-center mb-8">
           {(branding.login_logo_url || branding.logo_url) ? (
              <img src={branding.login_logo_url || branding.logo_url!} alt={branding.app_name} className="h-auto max-h-[180px] max-w-[380px] object-contain" />
            ) : (
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-primary/10">
                  {branding.icon_url ? (
                    <img src={branding.icon_url} alt="" className="h-8 w-8 object-contain" />
                  ) : (
                    <MessageSquare className="h-8 w-8 text-primary" />
                  )}
                </div>
                <span className="text-2xl font-bold text-foreground">{branding.app_name}</span>
              </div>
            )}
          </div>

          {/* Cabeçalho do formulário */}
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
              {isLogin ? 'Bem-vindo de volta!' : 'Crie sua conta'}
            </h2>
            <p className="text-muted-foreground">
              {isLogin ? 'Entre para acessar sua conta' : 'Comece gratuitamente em poucos segundos'}
            </p>
          </div>

          {/* Formulário */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Campo Nome (apenas cadastro) */}
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-sm font-medium">
                  Nome completo
                </Label>
                <div className="relative group">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground transition-colors group-focus-within:text-primary" />
                  <Input 
                    id="fullName" 
                    placeholder="Seu nome" 
                    value={fullName} 
                    onChange={e => setFullName(e.target.value)} 
                    className="pl-12 h-12 rounded-xl border-border/50 bg-background transition-all focus:ring-2 focus:ring-primary/20 focus:border-primary" 
                    disabled={loading} 
                  />
                </div>
                {errors.fullName && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <span className="inline-block w-1 h-1 rounded-full bg-destructive" />
                    {errors.fullName}
                  </p>
                )}
              </div>
            )}

            {/* Campo Telefone (apenas cadastro) */}
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium">
                  Telefone
                </Label>
                <div className="relative group">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground transition-colors group-focus-within:text-primary z-10" />
                  <PhoneInput 
                    id="phone"
                    value={phone}
                    onChange={setPhone}
                    placeholder="(11) 99999-9999"
                    disabled={loading}
                    className="[&>:first-child]:hidden [&>input]:pl-12 [&>input]:h-12 [&>input]:rounded-xl [&>input]:border-border/50"
                  />
                </div>
                {errors.phone && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <span className="inline-block w-1 h-1 rounded-full bg-destructive" />
                    {errors.phone}
                  </p>
                )}
              </div>
            )}

            {/* Campo Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email
              </Label>
              <div className="relative group">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground transition-colors group-focus-within:text-primary" />
                <Input 
                  id="email" 
                  type="email" 
                  placeholder="seu@email.com" 
                  value={email} 
                  onChange={e => setEmail(e.target.value)} 
                  className="pl-12 h-12 rounded-xl border-border/50 bg-background transition-all focus:ring-2 focus:ring-primary/20 focus:border-primary" 
                  disabled={loading} 
                />
              </div>
              {errors.email && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <span className="inline-block w-1 h-1 rounded-full bg-destructive" />
                  {errors.email}
                </p>
              )}
            </div>

            {/* Campo Senha */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">
                Senha
              </Label>
              <PasswordInput
                id="password"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete={isLogin ? 'current-password' : 'new-password'}
                className="h-12 rounded-xl border-border/50 bg-background transition-all focus:ring-2 focus:ring-primary/20 focus:border-primary"
                disabled={loading}
              />
              {/* Checklist de requisitos em tempo real (somente no cadastro) */}
              {!isLogin && password.length > 0 && <PasswordRequirements password={password} />}
              {errors.password && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <span className="inline-block w-1 h-1 rounded-full bg-destructive" />
                  {errors.password}
                </p>
              )}
            </div>

            {/* Campo Confirmar Senha (apenas cadastro) - padrão SaaS para evitar erro de digitação */}
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium">
                  Confirmar senha
                </Label>
                <PasswordInput
                  id="confirmPassword"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  className={`h-12 rounded-xl bg-background transition-all focus:ring-2 focus:ring-primary/20 ${
                    confirmPassword.length > 0
                      ? password === confirmPassword
                        ? 'border-emerald-500/60 focus:border-emerald-500'
                        : 'border-destructive focus:border-destructive'
                      : 'border-border/50 focus:border-primary'
                  }`}
                  disabled={loading}
                />

                {errors.confirmPassword && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <span className="inline-block w-1 h-1 rounded-full bg-destructive" />
                    {errors.confirmPassword}
                  </p>
                )}
              </div>
            )}


            {/* Link esqueceu senha (apenas login) */}
            {isLogin && (
              <div className="flex justify-end">
                <button 
                  type="button" 
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-primary hover:text-primary/80 transition-colors hover:underline"
                >
                  Esqueceu a senha?
                </button>
              </div>
            )}

            {/* Botão de submit */}
            <Button 
              type="submit" 
              className="w-full h-12 rounded-xl bg-gradient-to-r from-[#0a2540] to-[#1a3a5c] hover:from-[#0d3b66] hover:to-[#245f8a] text-white font-semibold text-base shadow-lg shadow-[#0a2540]/30 transition-all duration-300 hover:shadow-xl hover:shadow-[#0a2540]/40 hover:scale-[1.02] active:scale-[0.98]" 
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Carregando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {isLogin ? 'Entrar' : 'Criar conta'}
                  <ArrowRight className="h-5 w-5" />
                </span>
              )}
            </Button>
          </form>

          {/* Separador */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border/50" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-background px-4 text-sm text-muted-foreground">
                {isLogin ? 'Novo por aqui?' : 'Já tem uma conta?'}
              </span>
            </div>
          </div>

          {/* Toggle login/cadastro */}
          <Button 
            type="button" 
            variant="outline" 
            className="w-full h-12 rounded-xl border-border/50 hover:bg-accent transition-all duration-300" 
            onClick={() => {
              setIsLogin(!isLogin);
              setErrors({});
              setPassword('');
              setConfirmPassword('');
            }}
          >
            {isLogin ? 'Criar uma conta' : 'Fazer login'}
          </Button>
        </div>
      </div>

      {/* ========== MODAL ESQUECEU A SENHA ========== */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Recuperar senha</DialogTitle>
            <DialogDescription>
              Digite seu email e enviaremos um link para redefinir sua senha.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleForgotPassword} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="forgotEmail">Email</Label>
              <div className="relative group">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground transition-colors group-focus-within:text-primary" />
                <Input 
                  id="forgotEmail" 
                  type="email" 
                  placeholder="seu@email.com" 
                  value={forgotPasswordEmail} 
                  onChange={e => setForgotPasswordEmail(e.target.value)} 
                  className="pl-12"
                  disabled={forgotPasswordLoading} 
                />
              </div>
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotPasswordEmail('');
                }}
                disabled={forgotPasswordLoading}
              >
                Cancelar
              </Button>
              <Button 
                type="submit"
                disabled={forgotPasswordLoading || !forgotPasswordEmail}
              >
                {forgotPasswordLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Enviando...
                  </span>
                ) : (
                  'Enviar link'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Auth;
